<script setup>
import { provideSidebarContext } from './provider';
import { useKbd } from 'dashboard/composables/utils/useKbd';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useStorage } from '@vueuse/core';
import { useSidebarKeyboardShortcuts } from './useSidebarKeyboardShortcuts';
import { getContentSidebar } from './contentSidebar';

import Icon from 'dashboard/components-next/icon/Icon.vue';

import Button from 'dashboard/components-next/button/Button.vue';
import SidebarGroup from './SidebarGroup.vue';
import SidebarProfileMenu from './SidebarProfileMenu.vue';
import SidebarAccountSwitcher from './SidebarAccountSwitcher.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';

const emit = defineEmits([
  'closeKeyShortcutModal',
  'openKeyShortcutModal',
  'showCreateAccountModal',
  'changeNavbar',
]);

const searchShortcut = useKbd([`$mod`, 'k']);
const { t } = useI18n();

const toggleShortcutModalFn = show => {
  if (show) {
    emit('openKeyShortcutModal');
  } else {
    emit('closeKeyShortcutModal');
  }
};

useSidebarKeyboardShortcuts(toggleShortcutModalFn);

// We're using localStorage to store the expanded item in the sidebar
// This helps preserve context when navigating between portal and dashboard layouts
// and also when the user refreshes the page
const expandedItem = useStorage(
  'next-sidebar-expanded-item',
  null,
  sessionStorage
);

const setExpandedItem = name => {
  expandedItem.value = expandedItem.value === name ? null : name;
};
provideSidebarContext({
  expandedItem,
  setExpandedItem,
});

const globalConfig = useMapGetter('globalConfig/get');

const menuItems = getContentSidebar();
</script>

<template>
  <aside
    class="w-[250px] bg-n-navbar-background rounded-2xl rtl:border-l ltr:border-r border-n-weak h-full flex flex-col text-sm pb-1 py-4"
  >
    <div class="flex justify-end p-1">
      <button type="button" @click="emit('changeNavbar')">
        <Icon
          icon="i-lucide-arrow-left-from-line"
          class="size-4 text-n-slate-10 flex-shrink-0"
        />
      </button>
    </div>
    <section class="grid gap-4 px-7 mb-4">
      <div class="mx-auto">
        <img
          :src="globalConfig.logo"
          :alt="globalConfig.installationName"
          class="block dark:hidden object-contain w-auto h-12 mx-auto"
        />
        <img
          v-if="globalConfig.logoDark"
          :src="globalConfig.logoDark"
          :alt="globalConfig.installationName"
          class="hidden dark:block object-contain w-auto h-12 mx-auto"
        />
      </div>
      <SidebarAccountSwitcher
        class="flex-grow min-w-0 -mx-1"
        @show-create-account-modal="emit('showCreateAccountModal')"
      />
      <RouterLink
        :to="{ name: 'search' }"
        class="router-link-active router-link-exact-active flex items-center w-full gap-2 px-2 py-1 h-10 shadow-md bg-white rounded-md"
      >
        <span class="flex-shrink-0 i-lucide-search size-4 text-n-brand" />
        <span class="flex-grow text-left">
          {{ t('COMBOBOX.SEARCH_PLACEHOLDER') }}
        </span>
        <span
          class="hidden tracking-wide pointer-events-none select-none text-n-slate-10"
        >
          {{ searchShortcut }}
        </span>
      </RouterLink>
      <ComposeConversation align-position="right">
        <template #trigger="{ toggle }">
          <Button
            icon="i-lucide-plus"
            color="blue"
            variant="solid"
            size="md"
            :label="t('SIDEBAR.NEW_CHAT')"
            justify="between"
            class="bg-n-bran w-full"
            @click="toggle"
          />
        </template>
      </ComposeConversation>
    </section>
    <nav class="grid flex-grow gap-2 pb-5 overflow-y-scroll no-scrollbar">
      <ul class="flex flex-col gap-1.5 m-0 list-none">
        <SidebarGroup
          v-for="item in menuItems"
          :key="item.name"
          v-bind="item"
        />
      </ul>
    </nav>
    <section
      class="py-1 px-4 border-t border-n-weak shadow-[0px_-2px_4px_0px_rgba(27,28,29,0.02)] flex-shrink-0 flex justify-between gap-2 items-center"
    >
      <SidebarProfileMenu
        @open-key-shortcut-modal="emit('openKeyShortcutModal')"
      />
    </section>
  </aside>
</template>
